import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { Container } from "reactstrap";
import { LINKED_IN_BUSINESS_COLOR } from "../../../constants/socialColors.constant";
import { ChartMetric } from "../../../cores/analytics/interface/ChartMetric";
import { ChartContainer } from "./ChartContainer";
import { LineChart } from "./charts/LineChart";
import { LinkedInBusinessAnalyticsSnapshot } from "./linkedinBusinessAnalyticsTypes";

interface Props {
  snapshot: LinkedInBusinessAnalyticsSnapshot;
}

const toChart = (
  days: { label: string; value: number }[],
  label: string,
  color: string
): ChartMetric => ({
  labels: days.map((day) => day.label),
  datasets: [
    {
      label,
      color,
      data: days.map((day) => day.value),
      yAxisID: "left",
      fill: false,
      type: "line",
    },
  ],
});

export const LinkedInBusinessReportCharts = ({ snapshot }: Props) => {
  const intl = useIntl();

  const followersChart = useMemo(
    () =>
      toChart(
        snapshot.followersDaily.map((day) => ({
          label: day.label,
          value: day.followerTotal ?? 0,
        })),
        intl.formatMessage({ id: "app.linkedinBusinessAnalytics.followers" }),
        LINKED_IN_BUSINESS_COLOR
      ),
    [intl, snapshot.followersDaily]
  );

  const pageViewsChart = useMemo(
    () =>
      toChart(
        snapshot.pageViewsDaily.map((day) => ({
          label: day.label,
          value: day.pageViews ?? 0,
        })),
        intl.formatMessage({ id: "app.linkedinBusinessAnalytics.pageViews" }),
        LINKED_IN_BUSINESS_COLOR
      ),
    [intl, snapshot.pageViewsDaily]
  );

  const impressionsChart = useMemo(
    () =>
      toChart(
        snapshot.engagementDaily.map((day) => ({
          label: day.label,
          value: day.impressions ?? 0,
        })),
        intl.formatMessage({ id: "app.linkedinBusinessAnalytics.impressions" }),
        LINKED_IN_BUSINESS_COLOR
      ),
    [intl, snapshot.engagementDaily]
  );

  return (
    <Container fluid className="px-0">
      <ChartContainer
        id="linkedinBusinessChart"
        height={250}
        className="mb-4"
        title={intl.formatMessage({ id: "app.linkedinBusinessAnalytics.followers" })}
      >
        <LineChart {...followersChart} />
      </ChartContainer>

      <ChartContainer
        id="linkedinBusinessChart"
        height={250}
        className="mb-4"
        title={intl.formatMessage({ id: "app.linkedinBusinessAnalytics.pageViews" })}
      >
        <LineChart {...pageViewsChart} />
      </ChartContainer>

      <ChartContainer
        id="linkedinBusinessChart"
        height={250}
        className="mb-4"
        title={intl.formatMessage({ id: "app.linkedinBusinessAnalytics.impressions" })}
      >
        <LineChart {...impressionsChart} />
      </ChartContainer>
    </Container>
  );
};
